import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { announcementAPI } from "../lib/studentAPI";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Megaphone, Calendar, User, Bell, Circle } from "lucide-react";
import { toast } from "sonner";

export default function Announcements() {
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readAnnouncements, setReadAnnouncements] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Load read announcements from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('readAnnouncements');
      if (stored) {
        try {
          const parsedData: unknown = JSON.parse(stored);
          // Type guard to ensure it's an array of strings
          const stringArray = Array.isArray(parsedData) 
            ? parsedData.filter((item): item is string => typeof item === 'string')
            : [];
          const readSet = new Set<string>(stringArray);
          setReadAnnouncements(readSet);
        } catch (error) {
          console.error('Failed to parse read announcements:', error);
          setReadAnnouncements(new Set<string>());
        }
      }
    }, []);
  // Save read announcements to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('readAnnouncements', JSON.stringify([...readAnnouncements]));
  }, [readAnnouncements]);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  // Update unread count whenever announcements or readAnnouncements change
  useEffect(() => {
    const unread = announcements.filter(a => !readAnnouncements.has(a.id)).length;
    setUnreadCount(unread);
    
    // Update document title with unread count
    if (unread > 0) {
      document.title = `(${unread}) Announcements - Student Portal`;
    } else {
      document.title = "Announcements - Student Portal";
    }
  }, [announcements, readAnnouncements]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await announcementAPI.getAnnouncements();
      const data = res?.success ? res.data : res || [];
      const announcementsArray = Array.isArray(data) ? data : [];
      setAnnouncements(announcementsArray);
      
      // Check for new announcements and show toast
      const newAnnouncements = announcementsArray.filter(a => !readAnnouncements.has(a.id));
      if (newAnnouncements.length > 0) {
        toast.info(`📢 ${newAnnouncements.length} new announcement${newAnnouncements.length > 1 ? 's' : ''}!`, {
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Failed to load announcements:", error);
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (announcementId: string) => {
    setReadAnnouncements(prev => new Set([...prev, announcementId]));
  };

  const markAllAsRead = () => {
    const allIds = announcements.map(a => a.id);
    setReadAnnouncements(new Set(allIds));
    toast.success("All announcements marked as read");
  };

  const getTargetColor = (target: string) => {
    if (target === "ALL") return "bg-violet-500 text-white";
    return "bg-amber-500 text-white";
  };

  const getTargetLabel = (target: string) => {
    return target === "ALL" ? "All Students" : "Specific Course";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 pb-12">
      {/* Header with Back Button */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </Button>

          <div className="flex items-center gap-3">
            {/* Notification Bell with Unread Badge */}
            <div className="relative">
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-2.5 rounded-2xl">
                <Bell className="w-6 h-6" />
              </div>
              {unreadCount > 0 && (
                <div className="absolute -top-2 -right-2">
                  <div className="relative">
                    <Circle className="w-5 h-5 text-red-500 fill-red-500" />
                    <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">Announcements</h1>
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white animate-pulse">
                    {unreadCount} New
                  </Badge>
                )}
              </div>
              <p className="text-gray-500 text-sm">Stay updated with the latest news</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-10">
        {/* Mark All as Read Button */}
        {announcements.length > 0 && unreadCount > 0 && (
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              className="text-sm border-indigo-300 text-indigo-600 hover:bg-indigo-50"
            >
              Mark all as read
            </Button>
          </div>
        )}

        {announcements.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center shadow-sm border border-gray-100">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mb-6">
              <Megaphone className="w-10 h-10 text-indigo-600" />
            </div>
            <h3 className="text-2xl font-semibold mb-3 text-gray-800">No Announcements Yet</h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              When your admin posts important updates, they will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {announcements.map((a: any) => {
              const isUnread = !readAnnouncements.has(a.id);
              
              return (
                <div 
                  key={a.id}
                  onClick={() => markAsRead(a.id)}
                  className="cursor-pointer"
                >
                  <Card 
                    className={`overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-white relative ${
                      isUnread ? 'ring-2 ring-red-400 ring-offset-2' : ''
                    }`}
                  >
                    {/* Unread Indicator Dot */}
                    {isUnread && (
                      <div className="absolute top-4 right-4 z-10">
                        <div className="relative">
                          <Circle className="w-3 h-3 text-red-500 fill-red-500 animate-pulse" />
                        </div>
                      </div>
                    )}

                    {/* Colorful Top Accent Bar */}
                    <div className={`h-2 bg-gradient-to-r ${
                      isUnread 
                        ? 'from-red-500 via-red-400 to-red-500' 
                        : 'from-violet-500 via-indigo-500 to-purple-500'
                    }`} />

                    <CardHeader className="pb-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {isUnread && (
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            )}
                            <CardTitle className={`text-2xl leading-tight ${
                              isUnread ? 'text-gray-900 font-bold' : 'text-gray-800'
                            }`}>
                              {a.title}
                            </CardTitle>
                          </div>
                        </div>

                        <Badge 
                          className={`px-4 py-1.5 text-sm font-medium self-start ${getTargetColor(a.target)}`}
                        >
                          {getTargetLabel(a.target)}
                        </Badge>
                      </div>

                      {/* Meta Information */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 mt-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(a.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                        {a.admin?.name && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {a.admin.name}
                          </div>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <div className="prose prose-gray max-w-none">
                        <p className={`leading-relaxed whitespace-pre-wrap text-[15.5px] ${
                          isUnread ? 'text-gray-800 font-medium' : 'text-gray-700'
                        }`}>
                          {a.message}
                        </p>
                      </div>
                    </CardContent>

                    {/* Subtle Footer Accent */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mx-6 mb-6" />
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}